import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useRealmApp } from '../RealmApp';

export default function useSubcategoryMutations() {
  const app = useRealmApp();
  return {
    addSubcategory: useAddSubcategory(app.currentUser.id),
    updateSubcategory: useUpdateSubcategory(app.currentUser.id),
    deleteSubcategory: useDeleteSubcategory(app.currentUser.id),
  };
}

const AddSubcategoryMutation = gql`
  mutation AddSubcategory($subcategory: SubcategoryInsertInput!) {
    addedSubcategory: insertOneSubcategory(data: $subcategory) {
      _id
      name
      color
      categoryId
      updateDatetime
      creationDatetime
    }
  }
`;

const UpdateSubcategoryMutation = gql`
  mutation UpdateSubcategory(
    $subcategoryId: ObjectId!
    $ownerId: String!
    $updates: SubcategoryUpdateInput!
  ) {
    updatedSubcategory: updateOneSubcategory(
      query: { _id: $subcategoryId, owner_id: $ownerId }
      set: $updates
    ) {
      _id
      name
      color
      categoryId
      updateDatetime
      creationDatetime
    }
  }
`;

const DeleteSubcategoryMutation = gql`
  mutation DeleteSubcategory($subcategoryId: ObjectId!, $ownerId: String!) {
    deletedSubcategory: deleteOneSubcategory(
      query: { _id: $subcategoryId, owner_id: $ownerId }
    ) {
      _id
    }
  }
`;

const SubcategoryFieldsFragment = gql`
  fragment SubcategoryFields on Subcategory {
    _id
    name
    color
    categoryId
    updateDatetime
    creationDatetime
  }
`;

function useAddSubcategory(ownerId) {
  const [addSubcategoryMutation] = useMutation(AddSubcategoryMutation, {
    // Manually save added Subcategories into the Apollo cache so that Subcategory queries automatically update
    // For details, refer to https://www.apollographql.com/docs/react/data/mutations/#making-all-other-cache-updates
    update: (cache, { data: { addedSubcategory } }) => {
      cache.modify({
        fields: {
          subcategories: (existingSubcategories = []) => [
            ...existingSubcategories,
            cache.writeFragment({
              data: addedSubcategory,
              fragment: SubcategoryFieldsFragment,
            }),
          ],
        },
      });
    },
  });
  return async (subcategory) => {
    const {
      data: { addedSubcategory },
    } = await addSubcategoryMutation({
      variables: {
        subcategory: {
          owner_id: ownerId,
          ...subcategory,
        },
      },
    });
    return addedSubcategory;
  };
}

function useUpdateSubcategory(ownerId) {
  const [updateSubcategoryMutation] = useMutation(UpdateSubcategoryMutation);
  //
  return async (subcategory, updates) => {
    const {
      data: { updatedSubcategory },
    } = await updateSubcategoryMutation({
      variables: { subcategoryId: subcategory._id, ownerId, updates },
    });
    return updatedSubcategory;
  };
}

function useDeleteSubcategory(ownerId) {
  const [deleteSubcategoryMutation] = useMutation(DeleteSubcategoryMutation, {
    update: (cache, { data: { deletedSubcategory } }) => {
      cache.modify({
        fields: {
          subcategories: (existingSubcategories = [], { readField }) =>
            existingSubcategories.filter(
              (ref) => deletedSubcategory._id !== readField('_id', ref)
            ),
        },
      });
    },
  });
  return async (subcategory) => {
    const {
      data: { deletedSubcategory },
    } = await deleteSubcategoryMutation({
      variables: { subcategoryId: subcategory._id, ownerId },
    });
    return deletedSubcategory;
  };
}
